import { useApi } from 'api'
import { queryKeys } from 'api/queryKeys'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { routes } from 'app/routes'
import { ALL_INVOICES_PER_PAGE, ITEMS_PER_PAGE } from './constants'
import { DATE_API_FORMAT } from 'constants/dates'
import { tableColumns } from './structure'
import {
  applyIdSearch,
  applyCustomerSearch,
  applyAddressSearch,
  applyStatusFilter,
} from './helpers/filters'
import isOverdue from 'features/invoices/helpers/isOverdue'
import { buildDateConditions } from './translators/buildDateFilter'
import { buildSingleStatusConditions } from './translators/buildStatusFilter'
import { Button, Flex, Table } from 'antd'
import dayjs from 'dayjs'
import useInvoiceListFilters from './hooks/useInvoiceListFilters'
import TableSkeleton from 'shared/ui/Skeletons/TableSkeleton'

const InvoicesList = (): React.ReactElement => {
  const api = useApi()
  const { filters, filterConfig, handleTableChange } = useInvoiceListFilters()
  const {
    currentPage,
    searchId,
    searchCustomer,
    searchAddress,
    statusFilter,
    sort,
    dateFrom,
    dateTo,
    deadlineFrom,
    deadlineTo,
  } = filters

  const navigate = useNavigate()

  // Hybrid filtering strategy:
  //   Server-side path  — single status + date ranges → sent as API filter conditions, normal pagination.
  //   Client-side path  — text searches or multi-status → fetch ALL_INVOICES_PER_PAGE rows, filter in JS.
  // The API exposes no text search and no OR operator for multi-status, so the client-side fallback is unavoidable.
  const isSingleStatus = statusFilter.length === 1
  const hasClientSideFilter =
    !!(searchId || searchCustomer || searchAddress) || statusFilter.length > 1

  // When falling back to client-side filtering, always fetch page 1 at max size so JS predicates see the full dataset.
  const effectivePage = hasClientSideFilter ? 1 : currentPage
  const effectivePerPage = hasClientSideFilter
    ? ALL_INVOICES_PER_PAGE
    : ITEMS_PER_PAGE

  // Date ranges and single-status are translated into API filter conditions; multi-status is left out because the
  // API only supports AND-chained conditions, making OR across statuses impossible server-side.
  const allConditions = [
    ...buildDateConditions(dateFrom, dateTo, deadlineFrom, deadlineTo),
    ...(isSingleStatus ? buildSingleStatusConditions(statusFilter) : []),
  ]
  const combinedFilter =
    allConditions.length > 0 ? JSON.stringify(allConditions) : undefined

  const { data, isPending: isInitialLoad } = useQuery({
    // Query key excludes text-search values — those are applied in `select`, not sent to the API.
    queryKey: queryKeys.invoices.list({
      page: effectivePage,
      perPage: effectivePerPage,
      sort,
      dateFrom,
      dateTo,
      deadlineFrom,
      deadlineTo,
      statusFilter: isSingleStatus ? statusFilter[0] : undefined,
    }),
    queryFn: () =>
      api.getInvoices({
        page: effectivePage,
        per_page: effectivePerPage,
        sort,
        filter: combinedFilter,
      }),
    // `select` runs in-memory on the cached API response. For the server-side path, applyStatusFilter
    // is a no-op (server already filtered); for the client-side path, it filters the full 500-row batch.
    select: (responseData) => ({
      ...responseData.data,
      invoices: applyStatusFilter(
        applyAddressSearch(
          applyCustomerSearch(
            applyIdSearch(responseData.data.invoices ?? [], searchId),
            searchCustomer
          ),
          searchAddress
        ),
        statusFilter
      ),
    }),
    placeholderData: keepPreviousData,
  })

  if (isInitialLoad) return <TableSkeleton />

  return (
    <>
      <Flex justify="flex-end" className="mb-2">
        <Button type="primary" href={routes.invoices.new}>
          Create Invoice
        </Button>
      </Flex>
      <Table
        dataSource={data?.invoices ?? []}
        columns={tableColumns(filterConfig)}
        rowKey="id"
        onChange={handleTableChange}
        pagination={
          hasClientSideFilter
            ? false
            : data?.pagination && {
                total: data?.pagination?.total_entries ?? 0,
                pageSize: ITEMS_PER_PAGE,
                current: currentPage,
                showSizeChanger: false,
              }
        }
        rowClassName={(record) => {
          const today = dayjs().format(DATE_API_FORMAT)
          return isOverdue(record, today) ? 'table-danger' : ''
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e.metaKey || e.ctrlKey) {
              const win = window.open(routes.invoices.show(record.id), '_blank')
              win?.focus()
            } else {
              navigate(routes.invoices.show(record.id))
            }
          },
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              navigate(routes.invoices.show(record.id))
            }
          },
          role: 'button',
          tabIndex: 0,
          className: 'pe-auto',
        })}
      />
    </>
  )
}

export default InvoicesList
